import { useMemo } from 'react'
import { Combobox } from 'components/UI/combobox/combobox'
import dialogConfirm from 'components/dialogConfirm'
import { notifySuccess } from 'services/toast'
import { toast } from 'react-toastify'
import {
  useGetProductsCatalog,
  useBulkMatchVariants,
} from 'services/products_api'

const MAX_BULK_MATCH_LIMIT = 30

export interface LinkProductComboboxProps {
  selectedVariantIds: number[]
  unselectedVariantIds: number[]
  totalSelectedRows: number
  isAllRowsSelected: boolean
  filters: Record<string, any>
  onSuccess?: () => void
  buttonLess?: boolean
}

export function LinkProductCombobox(props: LinkProductComboboxProps) {
  const bulkMatchMutation = useBulkMatchVariants()

  // Fetch products for the dropdown
  const { data: productsData, isLoading } = useGetProductsCatalog({
    page: 1,
    page_size: 1000,
    sort: 'name',
  })

  // Convert products to combobox options
  const productOptions = useMemo(() => {
    if (!productsData?.results) return []
    return productsData.results.map((product: any) => ({
      label: product.name,
      value: product.id,
    }))
  }, [productsData])

  async function handleSubmit(productId: number | string) {
    // Enforce limit of 30 items for explicit mode (non-select-all)
    if (
      !props.isAllRowsSelected &&
      props.selectedVariantIds.length > MAX_BULK_MATCH_LIMIT
    ) {
      toast.warning(
        `Cannot link more than ${MAX_BULK_MATCH_LIMIT} variants at a time. Please select fewer variants.`
      )
      return
    }

    const confirmed = await dialogConfirm(
      `Confirm linking ${props.totalSelectedRows} variant${props.totalSelectedRows !== 1 ? 's' : ''} to this product?`,
      'Link to Product'
    )

    if (!confirmed) {
      return
    }

    try {
      // Find the product name for optimistic UI display
      const selectedProduct = productOptions.find(
        (p) => p.value === Number(productId)
      )
      const productName = selectedProduct?.label

      // If "select all" is active, use filter-based mode
      const requestBody = props.isAllRowsSelected
        ? {
            product_id: Number(productId),
            filters: props.filters,
            exclude_ids: props.unselectedVariantIds,
            _productName: productName,
          }
        : {
            matches: props.selectedVariantIds.map((variantId) => ({
              variant_id: variantId,
              product_id: Number(productId),
            })),
            _productName: productName,
          }

      const response = await bulkMatchMutation.mutateAsync(requestBody)

      // Show different message based on async vs sync response
      if (response?.async) {
        notifySuccess(
          `Linking ${props.totalSelectedRows} variant${props.totalSelectedRows !== 1 ? 's' : ''} in background. Changes will appear shortly.`
        )
      } else {
        const matchedCount = response?.matched_count || props.totalSelectedRows
        notifySuccess(
          `Successfully linked ${matchedCount} variant${matchedCount !== 1 ? 's' : ''} to product`
        )
      }

      // Call success callback to clear selection
      props.onSuccess?.()
    } catch (error) {
      console.error('Error in bulk match:', error)
      // Error notification handled by mutation hook
    }
  }

  return (
    <Combobox
      options={productOptions}
      searchPlaceholder="Search products..."
      placeholder="Link to Product"
      onSelectAltAction={handleSubmit}
      loading={isLoading}
      buttonLess={props.buttonLess}
      notFoundPlaceholder="No products found"
    />
  )
}
